import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui';
import { QuestionRouter } from './questions/QuestionRouter';
import type { QuestionDefinition, CheckinAnswerValue } from '../../../types/checkin';

interface MorningQuestionsProps {
  questions: QuestionDefinition[];
  currentIndex: number;
  responses: Record<string, CheckinAnswerValue>;
  onResponseChange: (questionId: string, value: CheckinAnswerValue) => void;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
}

export function MorningQuestions({
  questions,
  currentIndex,
  responses,
  onResponseChange,
  onNext,
  onPrevious,
  onSkip,
}: MorningQuestionsProps) {
  const currentQuestion = questions[currentIndex];
  const currentResponse = responses[currentQuestion.id];
  const isFirstQuestion = currentIndex === 0;
  const isLastQuestion = currentIndex === questions.length - 1;

  // Validate if current question has a response
  const hasResponse =
    currentResponse !== undefined &&
    currentResponse !== '' &&
    currentResponse !== null &&
    (Array.isArray(currentResponse) ? currentResponse.length > 0 : true);

  const progressPercentage = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="flex flex-col h-full max-w-2xl mx-auto">
      {/* Header with Skip button */}
      <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
        <button
          onClick={onSkip}
          className="p-2 text-surface-600 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-200 transition-colors rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          aria-label="Skip check-in"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <div className="text-sm font-medium text-surface-600 dark:text-surface-400">
          Question {currentIndex + 1} of {questions.length}
        </div>

        <div className="w-10" /> {/* Spacer for alignment */}
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-4">
        <div className="relative w-full h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-500 to-accent-600 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
          />
        </div>
      </div>

      {/* Question content */}
      <motion.div
        key={currentQuestion.id}
        className="flex-1 overflow-y-auto px-6 py-8"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
      >
        <div className="space-y-6">
          {/* Question text */}
          <div>
            <h2 className="text-2xl font-semibold text-surface-800 dark:text-white mb-2">
              {currentQuestion.text}
            </h2>
            {currentQuestion.subtext && (
              <p className="text-surface-600 dark:text-surface-400">
                {currentQuestion.subtext}
              </p>
            )}
          </div>

          {/* Question input component */}
          <div className="pt-4">
            <QuestionRouter
              question={currentQuestion}
              value={currentResponse}
              onChange={(value) => onResponseChange(currentQuestion.id, value)}
            />
          </div>
        </div>
      </motion.div>

      {/* Navigation buttons */}
      <div className="border-t border-surface-200 dark:border-surface-700 p-4 bg-white dark:bg-surface-900">
        <div className="flex gap-3">
          {!isFirstQuestion && (
            <Button
              onClick={onPrevious}
              variant="secondary"
              size="lg"
              leftIcon={<ChevronLeftIcon className="w-5 h-5" />}
              className="flex-shrink-0"
            >
              Previous
            </Button>
          )}

          <Button
            onClick={onNext}
            variant="primary"
            size="lg"
            disabled={!hasResponse}
            rightIcon={!isLastQuestion ? <ChevronRightIcon className="w-5 h-5" /> : undefined}
            fullWidth
          >
            {isLastQuestion ? 'Submit' : 'Next'}
          </Button>
        </div>

        {!hasResponse && (
          <p className="text-xs text-surface-500 dark:text-surface-500 text-center mt-3">
            Please answer the question to continue
          </p>
        )}
      </div>
    </div>
  );
}
